import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import i18n from '@/config/i18n.config'

import ForeignHealthHiringAnswers from '@/modules/foreign-health/hiring/models/APP/ForeignHealthHiringAnswers'
import ForeignHealthRaterAnswers from '../../rater/models/APP/ForeignHealthRaterAnswers'
import ENDPOINTS from '@/modules/core/constants/apiEndpoints'
import ForeignHealthConfigurations from '@/modules/foreign-health/common/models/APP/ForeignHealthConfigurations'
import ForeignHealthInsuranceProcessStepEnum from '@/modules/foreign-health/hiring/models/APP/ForeignHealthInsuranceProcessStepEnum'
import {
  ErrorDisplayTypeEnum,
  InsuranceProcessStatusEnum,
  UserRoleEnum
} from '@/modules/core/models/enums'
import MiddlewareService from '@/modules/core/services/MiddlewareService'
import ServiceError from '@/modules/core/models/errors/ServiceError'
import ModalButton from '@/modules/core/models/modalMessage/ModalButton'
import { useErrorManagerComposable } from '@/modules/core/composables/errorManagerComposable'
import { useToastStore } from '@/modules/core/stores/toastStore'
import { useLoadingStore } from '@/modules/core/stores/loadingStore'
import { useCommonStore } from '@/modules/core/stores/commonStore'
import DXPService from '@/modules/core/services/DXPService'
import ForeignHealthCommonMapper from '@/modules/foreign-health/common/mappers'
import ForeignHealthMapper from '@/modules/foreign-health/hiring/mappers'
import ProductId from '@/modules/common/common/models/DXP/ProductId'
import type DXPProduct from '@/modules/common/common/models/DXP/Product'
import CustomError from '@/modules/core/models/errors/CustomError'
import type ForeignHealthInsurance from '../../common/models/APP/ForeignHealthInsurance'
import type RaterPricing from '@/modules/common/common/models/MID/RaterPricing'
import { checkRecoveredQuotationBranch } from '@/modules/core/utils/BranchUtils'
import GetProductResponse from '@/modules/common/common/models/DXP/responses/GetProductResponse'
import ForeignHealthHiringSummary from '../models/APP/ForeignHealthHiringSummary'
import type Summary from '@/modules/common/hiring/models/APP/Summary'
import type FormPage from '@/modules/common/common/models/APP/FormPage'
import { usePagesBuilder } from '@/modules/core/composables/usePagesBuilderComposable'
import STUDENT_PAGES from '@/modules/foreign-health/hiring/constants/studentPages'
import RESIDENT_PAGES from '@/modules/foreign-health/hiring/constants/residentPages'
import { useNavigation } from '@/modules/core/composables/navigationComposable'
import CommonRequestsMapper from '@/modules/common/common/mappers/index'
import { GetPaymentUrlResponse } from '@/modules/common/common/models/MID/responses/GetPaymentUrlResponse'
import { isBigger } from '@/utils/date.utils'
import type AdditionalCoverageConfig from '@/modules/health/hiring/models/APP/AdditionalCoverageConfig'
import GetPricingMapper from '../../common/mappers/MID/GetPricingMapper'
import ForeignHealthInsured from '../models/MID/ForeignHealthInsured'
import DocumentDetails from '@/modules/common/hiring/models/APP/DocumentDetails'
import HealthMapper from '@/modules/core/mappers/HealthMapper'
import type { BillingCycles } from '@/modules/health/rater/models/DXP/ConfigurationsProperties'

export const useForeignHealthStore = defineStore('foreign-health', () => {
  const errorManager = useErrorManagerComposable()
  const toastStore = useToastStore()
  const loadingStore = useLoadingStore()
  const commonStore = useCommonStore()
  const pagesBuilder = usePagesBuilder()
  const navigation = useNavigation()

  const { t } = i18n.global

  const globalConfig = ref(new ForeignHealthConfigurations())
  const additionalCoveragesConfig = ref<AdditionalCoverageConfig[]>([])
  const hiringAnswers = ref(new ForeignHealthHiringAnswers())
  const hiringAnswersByGetPricing = ref(new ForeignHealthHiringAnswers())
  const pages = ref(STUDENT_PAGES)
  const navigationPages = ref<Record<string, FormPage>>({})
  let navigationPagesBackup = {} as Record<string, FormPage>
  const insuredsState = computed(() => {
    const insureds = hiringAnswers.value.insureds
    const insuredsComplete = insureds.map(
      (i) =>
        hiringAnswers.value.currentInsured?.riskNumber! > i.riskNumber &&
        (!!i.documentDetails.documentNumber || i.documentDetails.documentType === 'MENOR')
    )
    const holder = hiringAnswers.value.holder.role
    const index = holder === UserRoleEnum.HOLDER_NOT_INSURED ? 0 : 1

    return insuredsComplete.slice(index, insureds.length)
  })

  watch(
    () => hiringAnswers.value.currentInsured?.studentSituation,
    (newValue) => {
      if (!newValue) return
      if (newValue === 'N') {
        hiringAnswers.value.currentInsured!.extraDocument = new DocumentDetails()
      }
    }
  )

  watch(
    () => hiringAnswers.value.currentInsured?.riskNumber,
    (riskNumber) => {
      if (!riskNumber) return
      if (
        hiringAnswers.value.abroadOption !== 4 &&
        isCurrentRiskNumberTheHighest() &&
        hiringAnswers.value.currentInsured?.role === UserRoleEnum.INSURED
      ) {
        const index = hiringAnswers.value.insureds.findIndex(
          (insured) => insured.riskNumber === riskNumber
        )
        const insuredNumber =
          hiringAnswers.value.holder.role === UserRoleEnum.HOLDER_NOT_INSURED ? riskNumber : index
        if (!hasAtLeastOneStudentQuestion(riskNumber)) {
          _removePage(`Step2_${insuredNumber}_2_StudentQuestionForm`)
        } else {
          _addPage(`Step2_${insuredNumber}_2_StudentQuestionForm`)
        }
      }
    }
  )

  function getStepsBeforeSaveAndExit() {
    const stepCode = hiringAnswers.value.processStatus?.stepCode ?? 0
    const role = hiringAnswers.value.holder.role
    const insuranceStep = {
      label: t('copy_save-and-exit-modal_step-insurance'),
      check: true
    }
    const documentStep = {
      label: t('copy_save-and-exit-modal_step-document'),
      check: stepCode > ForeignHealthInsuranceProcessStepEnum.DOCUMENTS
    }

    const holderInsuredIndex = !insuredsState.value.length ? null : 1
    const holderStep = {
      label: t(
        `copy_save-and-exit-modal_step-holder-${role}${
          hiringAnswers.value.insureds.length > 1 && role !== UserRoleEnum.HOLDER_NOT_INSURED
            ? '-n'
            : ''
        }`,
        { n: holderInsuredIndex }
      ),
      check: stepCode > ForeignHealthInsuranceProcessStepEnum.HOLDER
    }

    const firstInsuredIndex = role === UserRoleEnum.HOLDER_INSURED ? 1 : 0
    const insuredsSteps = insuredsState.value.map((check, index) => {
      return {
        label: t('copy_save-and-exit-modal_step-insured', { n: ++index + firstInsuredIndex }),
        check: check || stepCode > ForeignHealthInsuranceProcessStepEnum.INSUREDS
      }
    })

    const questionnaireStep = {
      label: t('copy_save-and-exit-modal_step-questionnaire'),
      check: stepCode > ForeignHealthInsuranceProcessStepEnum.QUESTIONNAIRES
    }

    return [insuranceStep, documentStep, holderStep, ...insuredsSteps, questionnaireStep]
  }

  function isCurrentRiskNumberTheHighest(): boolean {
    const currentRiskNumber = hiringAnswers.value.currentInsured?.riskNumber
    if (!currentRiskNumber) return false

    const allRiskNumbers = hiringAnswers.value.insureds
      .map((insured) => insured.riskNumber)
      .filter((riskNumber) => riskNumber !== undefined && riskNumber !== null)

    if (allRiskNumbers.length === 0) return false

    return Math.max(...allRiskNumbers) === currentRiskNumber
  }

  function hasAtLeastOneStudentQuestion(riskNumber: number): boolean {
    return hiringAnswers.value.insureds.some(
      (insured) => insured.studentSituation === 'S' && insured.riskNumber !== riskNumber
    )
  }

  function getBranchConfiguration() {
    return globalConfig.value
  }

  function _getCurrentInsured(insuredIndex?: number | string) {
    if (insuredIndex === undefined) return null

    if (insuredIndex === 0 || insuredIndex === 'holder') return hiringAnswers.value.holder

    if (hiringAnswers.value.holder.role === UserRoleEnum.HOLDER_NOT_INSURED) {
      // aqui
      return hiringAnswers.value.insureds[(insuredIndex as number) - 1]
    }

    return hiringAnswers.value.insureds[insuredIndex as number]
  }

  function setCurrentInsuredAnswers(answers: Partial<ForeignHealthInsured>, insuredIndex: number) {
    if (
      insuredIndex === 0 &&
      hiringAnswers.value.insureds[0].role === UserRoleEnum.HOLDER_INSURED
    ) {
      hiringAnswers.value.updateHolderAttributes(answers)
      return
    }
    hiringAnswers.value.updateInsuredAttributes(insuredIndex, answers)
  }

  async function _fetchAdditionalCoveragesConfig(quotationId: string) {
    try {
      const endpoint = `${ENDPOINTS.MDW_GET_COVERAGES}?quotationId=${quotationId}`
      const { error, data } = await MiddlewareService.get(endpoint)
      if (error) {
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.MODAL).setButtons([
          new ModalButton({
            text: 'copy_button_emission_exit',
            navTo: 'exit'
          })
        ])
      }

      const middlewareData = data.coverageInfoData
      const dxpData = await _fetchDXPCoverages(middlewareData)
      additionalCoveragesConfig.value = _loadCompleteSheetCoverages(middlewareData, dxpData)
    } catch (error) {
      errorManager.handleError(error)
    }
  }

  async function _fetchDXPCoverages(middlewareData: any[]) {
    const endpoint = ENDPOINTS.DXP_GET_COVERAGES
    const coverages = middlewareData.map((c) => ({
      coverageCode: c.coverageCode
    }))
    const request = { language: 'es', coverages: coverages }
    const { error, data } = await DXPService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    return data
  }

  function _loadCompleteSheetCoverages(middlewareData: any[], dxpData: any[]) {
    const coverages: AdditionalCoverageConfig[] = []
    dxpData.forEach(({ coverage: DXPCoverage }) => {
      const middlewareCoverage = middlewareData.find(
        (p) => String(DXPCoverage.coverageId) === String(p.coverageCode)
      )

      if (middlewareCoverage) {
        const coverageData = { ...DXPCoverage, ...middlewareCoverage }
        coverages.push(ForeignHealthMapper.AdditionalCoveragesConfig.getCoverages(coverageData))
      }
    })

    return coverages
  }

  function setGloballyAnswers(answers: Partial<ForeignHealthHiringAnswers>) {
    hiringAnswers.value.updateAttributes(answers as any)
  }

  function getHiringAnswers(): ForeignHealthHiringAnswers {
    const answers = hiringAnswers.value as ForeignHealthHiringAnswers
    return answers
  }

  //** PRIVATE SUPPORT METHODS */
  async function _fetchDXPInsurances(products: ProductId[]): Promise<DXPProduct[]> {
    const endpoint = ENDPOINTS.DXP_GET_PRODUCT
    const request = ForeignHealthCommonMapper.GetProductsMapper.toRequest(products)
    const { error, data } = await DXPService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    return data.map((d: any) => new GetProductResponse(d).product)
  }

  function _loadCompleteSheetProducts(
    DXPProducts: DXPProduct[],
    MIDProducts: RaterPricing[]
  ): ForeignHealthInsurance[] {
    const products: ForeignHealthInsurance[] = []
    DXPProducts.forEach((DXPProduct) => {
      const MIDProduct = MIDProducts.find((p) => {
        return String(DXPProduct.productId) === String(p.productCode)
      })

      if (MIDProduct) {
        const insurance = ForeignHealthCommonMapper.GetProductsMapper.fromResponse(
          DXPProduct,
          MIDProduct
        )
        products.push(insurance)
      }
    })

    return products
  }

  async function loadConfiguration(reset = false) {
    try {
      if (globalConfig.value?.businessLineId && !reset) {
        hiringAnswers.value.businessLine = globalConfig.value.businessLineId
        return
      }

      const endpoint = ENDPOINTS.DXP_GET_BRANCH_FOREIGN_HEALTH
      const { error, data } = await DXPService.get(endpoint)
      if (error) {
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.MODAL)
      }

      globalConfig.value = new ForeignHealthConfigurations({
        ...data,
        billingCycles: data.billingCyclesResident
      })
      hiringAnswers.value.businessLine = globalConfig.value.businessLineId
    } catch (error) {
      loadingStore.stopLoading()
      errorManager.handleError(error)
    }
  }

  async function getNewRaterPrice(
    answers: ForeignHealthHiringAnswers,
    errorDisplayType: ErrorDisplayTypeEnum = ErrorDisplayTypeEnum.TOAST,
    isQuote: boolean = true,
    productCode: string | null | undefined = undefined
  ): Promise<ForeignHealthInsurance | null> {
    try {
      loadingStore.startLoading('common.loading.processing')
      const endpoint = ENDPOINTS.RATER_CALCULATE_PRICE_FOREIGN_HEALTH

      const commonConfig = commonStore.getCommonConfigs()
      if (UserRoleEnum.HOLDER_NOT_INSURED === answers.holder.role) {
        answers.holder!.addressDetails.postalCode ||= commonConfig?.defaultPostalCode ?? null
      } else {
        answers.holder!.addressDetails.postalCode = answers.policyHolderPostal
      }

      const request = ForeignHealthCommonMapper.GetPricingMapper.toRequest(answers, {
        isQuote,
        mediator: commonStore.infoMediator?.id
      })
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) throw new ServiceError(data)

      const products = data.listPricing.map((l: RaterPricing) => {
        return new ProductId({
          productCode: l.productCode,
          clientCode: l.clientCode
        })
      })
      const dxpData = await _fetchDXPInsurances(products)
      const insurances = _loadCompleteSheetProducts(dxpData, data.listPricing)
      return (
        (productCode ? insurances.find((i) => i.productCode === productCode) : insurances[0]) ??
        null
      )
    } catch (error) {
      if (error instanceof CustomError) {
        error.setDisplayType(errorDisplayType)
      }
      errorManager.handleError(error)

      return null
    } finally {
      loadingStore.stopLoading()
    }
  }

  async function hireProduct(
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: ForeignHealthInsurance,
    role: UserRoleEnum,
    endDate?: Date | null
  ) {
    let valid = true

    try {
      loadingStore.startLoading('common.loading.processing')

      if (!quotationId) throw new Error('Quotation id is required')

      const endpoint = ENDPOINTS.SAVE_QUOTATION_FOREIGN_HEALTH
      const request = ForeignHealthMapper.SaveQuotationMapper.toRequest(
        quotationId,
        startDate,
        billingCycle,
        product,
        role,
        endDate
      )
      // no crear cotizaciÃ³n en SENDA
      ;(request as any).isQuote = false
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) throw new ServiceError(data)
    } catch (error) {
      console.error(error)
      valid = false
    } finally {
      loadingStore.stopLoading()
    }

    return { valid }
  }

  async function modifyRate(
    answers: ForeignHealthRaterAnswers,
    errorDisplayType: ErrorDisplayTypeEnum = ErrorDisplayTypeEnum.TOAST
  ): Promise<ForeignHealthInsurance | null> {
    try {
      loadingStore.startLoading('common.loading.processing')

      const endpoint = ENDPOINTS.RATER_CALCULATE_PRICE
      const request = GetPricingMapper.toRequest(answers, {
        isQuote: false,
        mediator: commonStore.infoMediator?.id
      })
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) throw new ServiceError(data)

      const products = data.listPricing
      const insuranceProposals = await _getProposals(products)

      return insuranceProposals[0]
    } catch (error) {
      if (error instanceof CustomError) {
        error.setDisplayType(errorDisplayType)
      }
      errorManager.handleError(error)

      return null
    } finally {
      loadingStore.stopLoading()
    }
  }

  async function _getProposals(middlewareData: any[]): Promise<ForeignHealthInsurance[]> {
    const ids = middlewareData.map((i: any) => ({
      productCode: String(i.productCode),
      clientCode: i.clientCode
    }))
    if (ids.length === 0) {
      throw new Error(t('copy_rater-empty-error-modal_title')) // ??
    }

    const dxpData = await _fetchDXPInsurances(ids)

    const products: ForeignHealthInsurance[] = []
    dxpData.forEach((DXPProduct: any) => {
      const MIDProduct = middlewareData.find(
        (p) => String(DXPProduct.productId) === String(p.productCode)
      )

      if (MIDProduct) {
        const insurance = ForeignHealthCommonMapper.GetProductsMapper.fromResponse(
          DXPProduct,
          MIDProduct
        )
        products.push(insurance)
      }
    })

    return products
  }

  async function fetchHiringFormAnswers(quotationId: string) {
    try {
      commonStore.showLogo = false
      const endpoint = `${ENDPOINTS.GET_RECOVERY_FOREIGN_HEALTH}?quotationId=${quotationId}`
      const { error, data } = await MiddlewareService.get(endpoint)
      if (error) {
        const modalButtons = [new ModalButton({ text: 'copy_button_emission_exit', navTo: 'exit' })]
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.MODAL)
          .setButtons(modalButtons)
      }
      await commonStore.resolveMediatorFromRecovery(data.mediator?.mediator)
      await loadConfiguration(true)
      if (data.hiring.stayAbroadType === Number(globalConfig.value.insuranceTypes.student.id)) {
        globalConfig.value.billingCycles = globalConfig.value.billingCyclesStudent as BillingCycles
      } else {
        globalConfig.value.billingCycles = globalConfig.value.billingCyclesResident as BillingCycles
      }
      const branchConfig = getBranchConfiguration()
      const itsOk = await checkRecoveredQuotationBranch(
        data.hiring.businessLine,
        branchConfig.businessLineId,
        quotationId
      )
      if (!itsOk) return
      const { productCode, clientCode } = data.hiring
      const product = new ProductId({ productCode, clientCode })
      const dxpData = await _fetchDXPInsurances([product])
      if (!dxpData.length) {
        const modalButtons = [new ModalButton({ text: 'copy_button_emission_exit', navTo: 'exit' })]
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.MODAL)
          .setButtons(modalButtons)
      }

      const DXPProduct = dxpData[0]
      const MIDProduct = data.hiring
      const insurance = ForeignHealthCommonMapper.GetProductsMapper.fromResponse(
        DXPProduct,
        MIDProduct
      )
      hiringAnswers.value = ForeignHealthMapper.GetRecoveryMapper.fromResponse(
        data,
        insurance,
        globalConfig.value
      )
      // Guardar los hiringAnswers en una ref del store para usarlo solo en el servicio de getPricing, con los quotation a nulo
      hiringAnswersByGetPricing.value = ForeignHealthMapper.GetRecoveryMapper.fromResponse(
        data,
        insurance,
        globalConfig.value,
        true
      )
      // Lanzar pricing y carga de coverages en paralelo y continuar solo cuando ambas terminen
      // pricing necesita los hiringAnswers construidos arriba
      // Ensure hiringAnswers.value is a ForeignHealthHiringAnswers instance
      const pricingPromise = getNewRaterPrice(
        hiringAnswersByGetPricing.value as ForeignHealthHiringAnswers,
        ErrorDisplayTypeEnum.TOAST,
        false
      )
      const coveragesPromise = _fetchAdditionalCoveragesConfig(quotationId)

      const [pricingResult] = await Promise.all([pricingPromise, coveragesPromise])

      // Si pricing falla (getNewRaterPrice devuelve null) tratamos como error para detener el flujo
      if (!pricingResult) {
        throw new Error('Pricing failed')
      }
      //actualizamos a valor que teniamos en el getRecovery
      pricingResult.quotationNumber = String(data.hiring.quotationNumber)
      hiringAnswers.value.updateAttributes({
        price:
          pricingResult.prices[
            hiringAnswers.value.billingCycle as keyof typeof pricingResult.prices
          ]
      })
    } catch (error) {
      errorManager.handleError(error)
      loadingStore.stopLoading()
    } finally {
      commonStore.showLogo = true
    }
  }

  function getHiringFormAnswers(insuredIndex?: number): ForeignHealthHiringAnswers {
    const currentInsured = _getCurrentInsured(insuredIndex)
    const answers = hiringAnswers.value as ForeignHealthHiringAnswers
    const insured = currentInsured as ForeignHealthInsured
    answers.currentInsured = insured ? new ForeignHealthInsured(insured) : null

    return answers
  }

  async function sendPolicyHolderDocument(answers: ForeignHealthHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_DOCUMENT_FOREIGN_HEALTH
    const quotationId = answers.quotationId ?? ''
    const holder = answers.holder
    const request = await ForeignHealthMapper.SaveDocumentMapper.toHolderRequest(
      quotationId,
      holder
    )
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    _setStepProcess(ForeignHealthInsuranceProcessStepEnum.HOLDER)
    toastStore.successToast('health.saved-holder-data-and-document')
  }

  async function sendInsuredsDocument(answers: ForeignHealthHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_DOCUMENT_INSURED_FOREIGN_HEALTH
    const quotationId = answers.quotationId ?? ''
    const insured = answers.currentInsured as ForeignHealthInsured
    const request = await ForeignHealthMapper.SaveDocumentMapper.toInsuredRequest(
      quotationId,
      insured
    )
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    let toastMessage = 'health.saved-insured-identification-document'
    if (insured.extraDocument?.file) {
      toastMessage = 'health.saved-insured-all-documents'
    }

    toastStore.successToast(t(toastMessage, { riskNumber: insured.riskNumber }))
  }

  async function sendDocumentNumber(answers: ForeignHealthHiringAnswers) {
    if (answers.currentInsured?.studentSituation === 'S') return
    await sendDocument(answers)
  }

  async function sendDocument(answers: ForeignHealthHiringAnswers) {
    if (answers.currentInsured?.role === UserRoleEnum.INSURED) {
      await sendInsuredsDocument(answers)
    } else {
      await sendPolicyHolderDocument(answers)
    }
  }

  async function sendNotInsuredDocument(answers: ForeignHealthHiringAnswers) {
    if (answers.currentInsured?.role !== UserRoleEnum.HOLDER_NOT_INSURED) return
    await sendPolicyHolderDocument(answers)
  }

  const availableHiringMethods = {
    sendNotInsuredDocument,
    sendDocumentNumber,
    sendPolicyHolderDocument: sendDocument,
    checkExistClient,
    sendPolicyHolder,
    sendInsureds,
    goToPayment,
    sendQuestionnaire
  }
  async function callToHiringMethod(
    methodName: keyof typeof availableHiringMethods,
    answers: ForeignHealthHiringAnswers,
    errorDisplayType: ErrorDisplayTypeEnum = ErrorDisplayTypeEnum.TOAST
  ) {
    try {
      loadingStore.startLoading('common.loading.processing')

      if (methodName && methodName in availableHiringMethods) {
        await availableHiringMethods[methodName](answers)
      }
    } catch (error) {
      const err = error instanceof CustomError ? error.setDisplayType(errorDisplayType) : error
      errorManager.handleError(err)
      throw error
    } finally {
      loadingStore.stopLoading()
    }
  }
  function _setStepProcess(stepProcess: ForeignHealthInsuranceProcessStepEnum) {
    const currentStepCode = hiringAnswers.value.processStatus.stepCode ?? 0
    if (currentStepCode < stepProcess) {
      hiringAnswers.value.processStatus.stepCode = stepProcess
    }
  }

  async function checkExistClient(answers: ForeignHealthHiringAnswers) {
    const holder = answers.holder
    const endpoint = ENDPOINTS.MDW_HEALTH_EXIST_CLIENT
    const request = ForeignHealthMapper.ExistClientMapper.toRequest(holder)
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    const thirdPartiesAnswered = data.hasDataPD && data.clientExists
    holder.updateAttributes({ thirdPartiesAnswered })
    setGloballyAnswers({ holder })
  }

  async function sendPolicyHolder(answers: ForeignHealthHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_HOLDER_FOREIGN_HEALTH
    const quotationId = answers.quotationId ?? ''
    const holder = answers.holder
    const request = ForeignHealthMapper.SavePolicyHolderMapper.toRequest(quotationId, holder)
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    const onlyInsureHolder =
      answers.insureds.length === 1 && answers.holder.role === UserRoleEnum.HOLDER_INSURED
    const processStep = onlyInsureHolder
      ? ForeignHealthInsuranceProcessStepEnum.QUESTIONNAIRES
      : ForeignHealthInsuranceProcessStepEnum.INSUREDS

    _setStepProcess(processStep)

    toastStore.successToast(
      answers.holder.role === UserRoleEnum.HOLDER_INSURED
        ? 'health.saved-holder-and-others-data'
        : 'health.saved-holder-data'
    )
  }

  async function saveInsureds(answers: ForeignHealthHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_INSUREDS_FOREIGN_HEALTH
    const quotationId = answers.quotationId ?? ''

    if (answers.currentInsured?.role !== UserRoleEnum.INSURED) return

    // datos de contacto de un menor menor
    if (
      !answers.currentInsured!.userDetails!.email &&
      !isBigger(answers.currentInsured!.userDetails!.birthDate as Date, 18)
    ) {
      answers.currentInsured!.userDetails!.email = answers.holder.userDetails.email
    }
    if (
      !answers.currentInsured!.userDetails!.phone ||
      !answers.currentInsured!.userDetails!.prefix
    ) {
      answers.currentInsured!.userDetails!.phone = answers.holder.userDetails.phone
      answers.currentInsured!.userDetails!.prefix = answers.holder.userDetails.prefix
    }
    // /datos de contacto de un menor menor

    const request = ForeignHealthCommonMapper.SaveForeignHealthMapper.toRequest(quotationId, [
      answers.currentInsured
    ])
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    const isLastInsured =
      answers.insureds[answers.insureds.length - 1].riskNumber ===
      answers.currentInsured?.riskNumber
    if (isLastInsured) {
      _setStepProcess(ForeignHealthInsuranceProcessStepEnum.QUESTIONNAIRES)
    }
    toastStore.successToast(
      t('health.saved-insured-data', { riskNumber: answers.currentInsured?.riskNumber })
    )
  }

  async function sendInsureds(answers: ForeignHealthHiringAnswers) {
    if (answers.currentInsured?.role !== UserRoleEnum.INSURED) return
    await saveInsureds(answers)
  }

  async function goToPayment(answers: ForeignHealthHiringAnswers) {
    loadingStore.stopLoading()
    loadingStore.hideHeader()
    loadingStore.startLoadingWithOptions({
      overLoaderTexts: [t('foreign-health.go-to-payment')],
      icon: 'icon-padlock'
    })

    const request = CommonRequestsMapper.GetPaymentURLMapper.toRequest(
      answers.quotationId!,
      answers.paymentType
    )

    const endpoint = ENDPOINTS.GET_PAYMENT_URL
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    const response = new GetPaymentUrlResponse(data)
    window.location.href = response.urlPayment
  }

  async function emission(quotationId: string) {
    return await commonStore.fetchInsuranceState(
      quotationId,
      'hiring-foreign-health',
      ENDPOINTS.POLICY_EMISSION_FOREIGN_HEALTH
    )
  }

  function isProcessExpired(): boolean {
    const statusCode = hiringAnswers.value.processStatus.statusCode
    return statusCode === InsuranceProcessStatusEnum.EXPIRED
  }

  function getSummary(): Summary {
    return new ForeignHealthHiringSummary(hiringAnswers.value as ForeignHealthHiringAnswers, t)
  }

  function isFinished() {
    const stepCode = hiringAnswers.value?.processStatus?.stepCode
    if (!stepCode) throw new Error('Processs step code is required')

    return stepCode >= ForeignHealthInsuranceProcessStepEnum.PAYMENT
  }

  function getFirstPage(pages: Record<string, FormPage>) {
    if (isProcessExpired()) {
      return 'Step0_1_StartDateForm'
    }

    const insureds = hiringAnswers.value.insureds as ForeignHealthInsured[]
    const processStep = hiringAnswers.value.processStatus?.stepCode ?? null

    return navigation.getPageByStepProcess(
      pages,
      processStep,
      insureds,
      ForeignHealthInsuranceProcessStepEnum.INSUREDS
    )
  }

  function loadPages() {
    const expectedPages: Record<string, FormPage> = {}

    const holder = hiringAnswers.value.holder as ForeignHealthInsured
    const insureds = hiringAnswers.value.insureds as ForeignHealthInsured[]
    const insuredsToFill = pagesBuilder.getPageInsureds<ForeignHealthInsured>(holder, insureds)

    // TODO: Check how to do this better, config values and abroadOption do not match
    if (hiringAnswers.value.abroadOption === 4) {
      pages.value = RESIDENT_PAGES
    }

    const step1Pages = pages.value.STEP1_PAGES
    const step2Pages = pagesBuilder.getDynamicPages<ForeignHealthInsured>(
      pages.value.STEP2_PAGES,
      insuredsToFill
    )

    // Join step1 with step2
    if (Object.keys(step1Pages).length) {
      const step1FirstPage = pagesBuilder.getFirstPageName(step1Pages)
      const step1LastPage = pagesBuilder.getLastPageName(step1Pages)
      const step2FirstPage = pagesBuilder.getFirstPageName(step2Pages)

      step1Pages[step1FirstPage].next[0] = step2FirstPage
      step1Pages[step1LastPage].next = [step2FirstPage]
    }

    // Join step2 with step3
    if (Object.keys(step2Pages).length) {
      const step2LastPage = pagesBuilder.getLastPageName(step2Pages)
      const step3FirstPage = pagesBuilder.getFirstPageName(pages.value.STEP3_PAGES)
      step2Pages[step2LastPage].next[0] = step3FirstPage
    }

    if (isProcessExpired()) {
      Object.assign(expectedPages, pages.value.STEP0_PAGES)
    }

    Object.assign(expectedPages, step1Pages)
    Object.assign(expectedPages, step2Pages)

    const step3Pages = pages.value.STEP3_PAGES
    Object.assign(expectedPages, step3Pages)

    navigationPages.value = expectedPages
    navigationPagesBackup = Object.keys(expectedPages).reduce(
      (acc, key) => {
        acc[key] = {
          ...expectedPages[key],
          next: [...expectedPages[key].next],
          extra: { ...expectedPages[key].extra }
        }
        return acc
      },
      {} as Record<string, FormPage>
    )
    if (holder.role === UserRoleEnum.HOLDER_INSURED && insureds.length < 2) {
      _removePage(`Step2_0_2_StudentQuestionForm`)
      // hiringAnswers.value.holder.studentSituation = 'S'
    }
    return navigationPages.value
  }

  function getBranchName(): string {
    return 'foreign-health'
  }

  function getRePricingPages() {
    return pages.value.STEP0_PAGES
  }

  function checkIfNeedsRecalculate() {
    // hiringAnswers.value.startDate.setHours(0, 0, 0, 0)

    // const today = addDaysToDate(-7)
    // today.setHours(0, 0, 0, 0)
    // return hiringAnswers.value.startDate < today
    // this flow has his own step if needs to recalculate
    return false
  }

  function hasFinishedInsuredSteps(processStep: ForeignHealthInsuranceProcessStepEnum) {
    return processStep >= ForeignHealthInsuranceProcessStepEnum.INSUREDS
  }

  function getAdditionalCoveragesConfig() {
    return additionalCoveragesConfig.value
  }

  function isEmitted() {
    const stepCode = hiringAnswers.value?.processStatus?.stepCode
    if (!stepCode) throw new Error('Processs step code is required')

    return stepCode === ForeignHealthInsuranceProcessStepEnum.EMISSION
  }

  async function sendQuestionnaire(answers: ForeignHealthHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_QUESTIONNAIRE
    const quotationId = answers.quotationId ?? ''
    const insureds = answers.insureds as ForeignHealthInsured[]
    const request = HealthMapper.toQuestionnaireRequest(quotationId, insureds)
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    _setStepProcess(ForeignHealthInsuranceProcessStepEnum.SIGN)
    toastStore.successToast('health.saved-all-data')
  }

  function _removePage(pageName: string) {
    if (navigationPages.value[pageName]) {
      const page = navigationPages.value[pageName]
      for (const key in navigationPages.value) {
        if (navigationPages.value[key].next.includes(pageName)) {
          navigationPages.value[key].next = [...page.next]
        }
      }
      delete navigationPages.value[pageName]
    }
  }

  function _addPage(pageName: string) {
    if (!navigationPagesBackup[pageName] || navigationPages.value[pageName]) return

    const pageToRestore = navigationPagesBackup[pageName]
    navigationPages.value[pageName] = {
      form: pageToRestore.form,
      next: [...pageToRestore.next],
      extra: { ...pageToRestore.extra }
    }

    for (const key in navigationPagesBackup) {
      if (key !== pageName && navigationPagesBackup[key].next.includes(pageName)) {
        if (navigationPages.value[key]) {
          navigationPages.value[key].next = [...navigationPagesBackup[key].next]
        }
      }
    }
  }

  return {
    pages: navigationPages,
    getStepsBeforeSaveAndExit,

    getBranchConfiguration,
    loadConfiguration,
    isFinished,
    setCurrentInsuredAnswers,
    setGloballyAnswers,
    getHiringAnswers,
    loadPages,
    getNewRaterPrice,
    getRePricingPages,
    hireProduct,
    getSummary,
    isProcessExpired,
    fetchHiringFormAnswers,
    callToHiringMethod,
    emission,
    getFirstPage,
    getBranchName,
    checkIfNeedsRecalculate,
    hasFinishedInsuredSteps,
    getAdditionalCoveragesConfig,
    modifyRate,
    getHiringFormAnswers,
    isEmitted
  }
})
